import useDetectBrowser from '@/hooks/useDetectBrowser'
import useFetchPriceDropData from '@/hooks/useFetchPriceDropData'
import { Carousel } from '@pcln/horizon'
import { Loader } from '@pcln/loader'
import { PromoListingsCard } from '@pcln/marketing'
import { motion } from 'motion/react'
import { Box, Flex, Heading } from 'pcln-design-system'
import { useEffect, useMemo } from 'react'
import { calculateTripDuration, fireGA4ViewPromotionEvent, formatDate, handleClickOnPromoCard } from './utils'

const MotionDiv = motion.div

export default function FlyPriceDrop() {
  const { priceDropData, progress } = useFetchPriceDropData()
  const isBrowser = useDetectBrowser()
  const showPriceDrop = priceDropData.length >= 2

  useEffect(() => {
    if (showPriceDrop) {
      fireGA4ViewPromotionEvent()
    }
  }, [showPriceDrop])

  const slides = useMemo(
    () =>
      priceDropData.map((data, index) => {
        const {
          dealPrice,
          departDate,
          destinationAirportCode,
          destinationCity,
          destinationImage,
          landingPageUrl,
          originAirportCode,
          percentageDrop,
          returnDate,
          strikePrice,
        } = data ?? {}

        const formattedDepartDate = formatDate(departDate)
        const formattedReturnDate = formatDate(returnDate || departDate)
        const tripDuration = calculateTripDuration(departDate, returnDate)

        return {
          id: `fly-price-drop-${index + 1}`,
          title: `${destinationCity} price drop deal`,
          childrenNodes: (
            <PromoListingsCard
              buttonText='Search Flight Times'
              dealPrice={dealPrice}
              departDate={formattedDepartDate}
              destinationAirportCode={destinationAirportCode}
              destinationCity={destinationCity}
              destinationImg={`${destinationImage}?opto&auto=avif,webp&width=192&height=144`}
              handleClick={() => handleClickOnPromoCard(landingPageUrl)}
              originAirportCode={originAirportCode}
              returnDate={formattedReturnDate}
              strikePrice={strikePrice}
              savingsText={`${percentageDrop}% PRICE DROP`}
              tripDuration={tripDuration}
              tripType='Round Trip'
            />
          ),
        }
      }),
    [priceDropData],
  )

  if (progress === 'PENDING') {
    return (
      <Flex justifyContent='space-around' data-testid='fly-price-drop-loader-dots' height='120px'>
        <Box mt={4} width='90px'>
          {isBrowser && <Loader />}
        </Box>
      </Flex>
    )
  }

  if (!showPriceDrop) {
    return null
  }

  return (
    <MotionDiv initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}>
      <Heading color='text.heading' mb={3} textStyle='heading3'>
        Price Drop Deals Near You
      </Heading>
      <Carousel
        ariaLabel='Price drop deals near you'
        dragFree
        numSlides='one-two-three'
        slides={slides}
        slideWidth='same'
        pagination='numbers'
        type='content'
      />
    </MotionDiv>
  )
}
